/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { Icon } from "@coreai/component-library";
import ReactMarkdown from "react-markdown";
import { css, useTheme } from "@emotion/react";
import { FaqCategory } from "../../../services/useFaqService";

interface AccordionItemProps {
  id: string;
  question: string;
  reponse: string;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionItem: React.FC<AccordionItemProps> = ({
  id,
  question,
  reponse,
  isOpen,
  onToggle,
}) => {
  const theme = useTheme();

  const itemStyles = css`
    border-bottom: 1px solid ${theme.colors.strokeGray};
    width: 100%;
  `;

  const headerStyles = css`
    display: flex;
    justify-content: space-between;
    padding: 15px;
    cursor: pointer;
    font-family: ${theme.typography.heading4_semiBold.fontFamily};
    font-size: ${theme.typography.heading4_semiBold.fontSize};
    font-weight: ${theme.typography.heading4_semiBold.fontWeight};
    color: ${theme.colors.textDark};
  `;

  const questionStyles = css`
    font-family: ${theme.typography.heading4_semiBold.fontFamily};
    font-size: ${theme.typography.heading4_semiBold.fontSize};

    font-weight: ${isOpen
      ? theme.typography.heading4_semiBold.fontWeight
      : 500};
  `;

  const contentStyles = css`
    padding: 0px 15px 15px 15px;
  `;

  return (
    <div css={itemStyles} id={id}>
      <div css={headerStyles} onClick={onToggle}>
        <span css={questionStyles}>{question}</span>
        <span>
          {isOpen ? (
            <Icon name="chevron-up" size="sm" />
          ) : (
            <Icon name="chevron-down" size="sm" />
          )}
        </span>
      </div>
      {isOpen && <ReactMarkdown css={contentStyles}>{reponse}</ReactMarkdown>}
    </div>
  );
};

interface AccordionProps {
  categories: FaqCategory[];
}

const Accordion: React.FC<AccordionProps> = ({ categories }) => {
  const theme = useTheme();
  const [openIndex, setOpenIndex] = useState<string | null>(null);

  const containerStyles = css`
    width: 100%;
    margin: 20px auto;
  `;

  const cardStyles = css`
    border-radius: 8px;
    padding: 40px;
    margin-bottom: 36px;
    margin-top: 12px;
    position: relative;
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.shallow};
  `;

  const headingStyles = css`
    font-family: ${theme.typography.heading3_medium.fontFamily};
    font-size: ${theme.typography.heading3_medium.fontSize};
    font-weight: ${theme.typography.heading3_medium.fontWeight};
    color: ${theme.typography.heading3_medium.colors};
  `;

  const handleToggle = (index: string) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div css={containerStyles}>
      {categories && categories.length > 0 ? (
        categories?.sort((a,b)=> a?.position - b?.position).map((category, categoryIndex) => (
          <div key={categoryIndex} css={cardStyles}>
            <h3 css={headingStyles} id={category.slug}>
              {category.title}
            </h3>
            {category.items_faqs.map((item, itemIndex) => (
              <AccordionItem
                key={item.id}
                id={item.slug}
                question={item.title}
                reponse={item.content}
                isOpen={openIndex === `${categoryIndex}-${itemIndex}`}
                onToggle={() => handleToggle(`${categoryIndex}-${itemIndex}`)}
              />
            ))}
          </div>
        ))
      ) : (
        <p>Aucune catégorie disponible</p>
      )}
    </div>
  );
};

export default Accordion;
